/**
 * Translation Service - Uses Google Cloud Translation API for dynamic content
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for translations to reduce API calls
const translationCache = new Map();

/**
 * Generate cache key for translation
 */
const getCacheKey = (text, targetLang) => `${text}_${targetLang}`;

/**
 * Translate a single text
 * @param {string} text - Text to translate
 * @param {string} targetLanguage - Target language code (en, te, hi, kn)
 * @param {string} sourceLanguage - Source language code (optional)
 * @returns {Promise<string>} Translated text
 */
export const translateText = async (text, targetLanguage, sourceLanguage = null) => {
  // Return original if target is English or text is empty
  if (!text || targetLanguage === 'en') {
    return text;
  }

  // Check cache first
  const cacheKey = getCacheKey(text, targetLanguage);
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  try {
    const response = await fetch(`${API_URL}/api/translate/text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        target_language: targetLanguage,
        source_language: sourceLanguage,
      }),
    });

    if (!response.ok) {
      console.error('Translation API error:', response.status);
      return text; // Return original on error
    }

    const data = await response.json();
    const translated = data.translated || text;
    
    // Cache the result
    translationCache.set(cacheKey, translated);
    
    return translated;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original on error
  }
};

/**
 * Translate multiple texts in batch
 * @param {string[]} texts - Array of texts to translate
 * @param {string} targetLanguage - Target language code
 * @param {string} sourceLanguage - Source language code (optional)
 * @returns {Promise<string[]>} Array of translated texts
 */
export const translateBatch = async (texts, targetLanguage, sourceLanguage = null) => {
  // Return original if target is English
  if (targetLanguage === 'en') {
    return texts;
  }

  // Filter out empty texts and check cache
  const textsToTranslate = [];
  const indices = [];
  const results = [...texts];

  texts.forEach((text, index) => {
    if (!text) {
      results[index] = '';
      return;
    }

    const cacheKey = getCacheKey(text, targetLanguage);
    if (translationCache.has(cacheKey)) {
      results[index] = translationCache.get(cacheKey);
    } else {
      textsToTranslate.push(text);
      indices.push(index);
    }
  });

  // If all texts were cached, return results
  if (textsToTranslate.length === 0) {
    return results;
  }

  try {
    const response = await fetch(`${API_URL}/api/translate/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        texts: textsToTranslate,
        target_language: targetLanguage,
        source_language: sourceLanguage,
      }),
    });

    if (!response.ok) {
      console.error('Batch translation API error:', response.status);
      return texts; // Return original on error
    }

    const data = await response.json();
    
    // Map translations back to results
    data.translations.forEach((translation, i) => {
      const originalIndex = indices[i];
      let translated = translation.translated || textsToTranslate[i];
      // Decode HTML entities from Google Translate
      const textarea = document.createElement('textarea');
      textarea.innerHTML = translated;
      translated = textarea.value;
      results[originalIndex] = translated;
      
      // Cache the result
      const cacheKey = getCacheKey(textsToTranslate[i], targetLanguage);
      translationCache.set(cacheKey, translated);
    });

    return results;
  } catch (error) {
    console.error('Batch translation error:', error);
    return texts; // Return original on error
  }
};

/**
 * Translate scheme details
 * @param {string} schemeId - Scheme ID
 * @param {string} targetLanguage - Target language code
 * @returns {Promise<object>} Translated scheme details
 */
export const translateScheme = async (schemeId, targetLanguage) => {
  if (targetLanguage === 'en') {
    return null; // No translation needed
  }

  try {
    const response = await fetch(`${API_URL}/api/translate/scheme`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        scheme_id: schemeId,
        target_language: targetLanguage,
      }),
    });

    if (!response.ok) {
      console.error('Scheme translation API error:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Scheme translation error:', error);
    return null;
  }
};

/**
 * Translate news article
 * @param {string} title - News title
 * @param {string} description - News description
 * @param {string} targetLanguage - Target language code
 * @returns {Promise<object>} Translated news
 */
export const translateNews = async (title, description, targetLanguage) => {
  if (targetLanguage === 'en') {
    return { title, description };
  }

  try {
    const params = new URLSearchParams({
      title,
      description,
      target_language: targetLanguage,
    });

    const response = await fetch(`${API_URL}/api/translate/news?${params}`, {
      method: 'POST',
    });

    if (!response.ok) {
      console.error('News translation API error:', response.status);
      return { title, description };
    }

    const data = await response.json();
    return {
      title: data.translated_title || title,
      description: data.translated_description || description,
    };
  } catch (error) {
    console.error('News translation error:', error);
    return { title, description };
  }
};

/**
 * Check translation service status
 * @returns {Promise<object>} Service status
 */
export const getTranslationStatus = async () => {
  try {
    const response = await fetch(`${API_URL}/api/translate/status`);
    return await response.json();
  } catch (error) {
    console.error('Translation status error:', error);
    return { initialized: false };
  }
};

/**
 * Clear local translation cache
 */
export const clearCache = () => {
  translationCache.clear();
};

export default {
  translateText,
  translateBatch,
  translateScheme,
  translateNews,
  getTranslationStatus,
  clearCache,
};
